/**
 * formatApiError · v2.6.9 batch BM-3
 * ====================================
 * Turn an axios error into a single, dispatcher-friendly string that
 * surfaces the actual underlying problem instead of a generic toast.
 *
 * Designed for production debugging: when riders / drivers hit a real
 * failure on bullertaxi.com we want the toast to read
 *   "Quote failed · HTTP 500 · Maps error: REQUEST_DENIED"
 * NOT
 *   "Could not fetch quote"
 *
 * Also logs the full axios error to console so DevTools captures the
 * stack + request payload for follow-up.
 */
export function formatApiError(err, fallback = "Request failed") {
  // Always log the full object so the user can copy-paste from DevTools.
  // eslint-disable-next-line no-console
  console.error("[apiError]", err);

  if (!err) return fallback;

  // Network-level failure (no response at all — CORS, DNS, timeout, offline).
  if (!err.response) {
    const code = err.code ? ` · ${err.code}` : "";
    const msg = err.message || "Network error";
    return `${fallback} · ${msg}${code}`;
  }

  const status = err.response.status;
  const data = err.response.data;
  let detail = "";

  if (data && typeof data === "object") {
    // FastAPI uses `detail`; we sometimes nest it as `{code, message}`.
    if (typeof data.detail === "string") detail = data.detail;
    else if (data.detail && typeof data.detail === "object") {
      detail = data.detail.message || data.detail.code || JSON.stringify(data.detail);
    } else if (typeof data.message === "string") detail = data.message;
    else if (Array.isArray(data.detail)) {
      // Pydantic validation errors come back as a list of {loc, msg, type}.
      detail = data.detail.map((d) => d.msg || JSON.stringify(d)).join("; ");
    }
  } else if (typeof data === "string" && data.trim()) {
    detail = data.slice(0, 240);
  }

  const detailPart = detail ? ` · ${detail}` : "";
  return `${fallback} · HTTP ${status}${detailPart}`;
}
